import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import useWorkspaces from 'src/hooks/useWorkspaces'
import UserType from 'src/model/user-types'
import WorkspaceEdit from 'src/components/workspace-edit'
import { messageBoxShowAction } from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { useWorkspaceBranding } from 'src/utils/workspace-logo'
import { mediaUrl } from 'src/pages/collection/portal-page/brand-fields'
import styles from './styles'

const AVATAR_COLORS = ['#015FFB', '#002E47', '#7A4DFF', '#1D9341', '#B06E01']

const getInitials = name =>
  (name || '')
    .replace(/'s\b/, '')
    .split(' ')
    .filter(Boolean)
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

/** The workspace logo (Workspace › Branding), else its initials on a color. */
const WorkspaceAvatar = ({ logo, name, color, className, classes }) =>
  logo ? (
    <span className={`${className} ${classes.avatarLogo}`}>
      <img src={mediaUrl(logo)} alt="" />
    </span>
  ) : (
    <span className={className} style={{ background: color }}>
      {getInitials(name)}
    </span>
  )

const WorkspaceMenu = ({ classes }) => {
  const dispatch = useDispatch()
  const me = useSelector(state => state.authentication?.me)
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspaces()
  // The open workspace's branding, updated as soon as Branding saves a logo.
  const branding = useWorkspaceBranding()
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    const handleClickOutside = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  if (!currentWorkspace) {
    return null
  }

  // Display name: Standard reads as Trial, Pro v2 as Pro.
  const plan = UserType.getDisplayRoleName(me?.userRoleId)
  const role = me?.permissionPreset
  const meta = [plan, role].filter(Boolean).join(' · ')
  const canManageWorkspace = role !== 'Viewer' && role !== 'Editor'
  const currentIndex = Math.max(
    0,
    workspaces.findIndex(item => item.shopId === currentWorkspace.shopId),
  )
  const logoOf = workspace =>
    workspace.shopId === currentWorkspace.shopId && branding
      ? branding.logo
      : workspace.logo

  const handlePick = workspace => {
    setOpen(false)
    if (workspace.shopId !== currentWorkspace.shopId) {
      switchWorkspace(workspace.shopId)
    }
  }

  const handleEditName = () => {
    setOpen(false)
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: 'Edit Workspace',
          draggable: false,
          maxWidth: '500px',
          component: (
            <WorkspaceEdit
              currentWorkspaceName={currentWorkspace.shopName}
              currentWorkspaceId={currentWorkspace.shopId}
            />
          ),
        }),
      ),
    )
  }

  return (
    <div className={classes.root} ref={rootRef}>
      <button
        type="button"
        className={classes.trigger}
        style={{ background: open ? '#fff' : undefined }}
        onClick={() => setOpen(value => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <WorkspaceAvatar
          logo={logoOf(currentWorkspace)}
          name={currentWorkspace.shopName}
          color={AVATAR_COLORS[currentIndex % AVATAR_COLORS.length]}
          className={classes.triggerAvatar}
          classes={classes}
        />
        {currentWorkspace.shopName}
        {meta && <span className={classes.triggerMeta}>{meta}</span>}
        <Icon icon="tabler:selector" className={classes.triggerIcon} />
      </button>

      {open && (
        <div className={classes.menu} role="menu">
          <div className={classes.menuLabel}>Workspaces</div>
          {workspaces.map((workspace, index) => {
            const isCurrent = workspace.shopId === currentWorkspace.shopId

            return (
              <button
                key={workspace.shopId}
                type="button"
                role="menuitem"
                className={classes.option}
                style={{ background: isCurrent ? '#F4F6F8' : undefined }}
                onClick={() => handlePick(workspace)}
              >
                <WorkspaceAvatar
                  logo={logoOf(workspace)}
                  name={workspace.shopName}
                  color={AVATAR_COLORS[index % AVATAR_COLORS.length]}
                  className={classes.optionAvatar}
                  classes={classes}
                />
                <span className={classes.optionText}>
                  <span className={classes.optionName}>
                    {workspace.shopName}
                  </span>
                  {isCurrent && meta && (
                    <span className={classes.optionMeta}>{meta}</span>
                  )}
                </span>
                {isCurrent && (
                  <Icon icon="tabler:check" className={classes.optionCheck} />
                )}
              </button>
            )
          })}

          {canManageWorkspace && (
            <>
              <div className={classes.divider} />
              <button
                type="button"
                role="menuitem"
                className={classes.action}
                onClick={handleEditName}
              >
                <span className={classes.actionIcon}>
                  <Icon icon="tabler:pencil" />
                </span>
                Rename workspace
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default withStyles(styles)(WorkspaceMenu)
